import { useRef, useState } from 'react'
import {
  FileText,
  Plus,
  Loader2,
  ExternalLink,
  Check,
  ChevronDown,
  Paperclip,
} from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { DetailCard } from '@/admin/components/shared/detail-card'
import {
  useClaimAttachments,
  useDocumentCategories,
  useUploadClaimDocument,
  type Attachment,
} from '@/admin/pages/claims/use-claim-attachments'
import { toastApiError } from '@/shared/lib/api-error'

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*'

function attachmentType(a: Attachment): string {
  return a.requirement_key || a.document_option_key || 'other'
}

/**
 * Documentation - a compact, collapsible checklist of the supporting documents
 * the claim's type expects (the document categories whose `claim_types` name
 * it), plus an "Other" slot where the user can name and attach anything else.
 * Documents are the non-image attachments on the claim; photos live in Evidence.
 */
export function ClaimDocumentationCard({
  claimUuid,
  claimType,
  canEdit,
}: {
  claimUuid: string
  claimType: string
  canEdit: boolean
}) {
  const { data: categories } = useDocumentCategories()
  const expected = (categories ?? [])
    .filter((c) => c.claim_types?.includes(claimType))
    .map((c) => ({ value: c.key, label: c.label }))
  const expectedKeys = new Set(expected.map((e) => e.value))
  const { data: attachments } = useClaimAttachments(claimUuid)
  const uploadDoc = useUploadClaimDocument(claimUuid)
  const inputRef = useRef<HTMLInputElement>(null)
  const pendingType = useRef<string>('other')
  const [open, setOpen] = useState(false)
  const [busyType, setBusyType] = useState<string | null>(null)
  const [otherName, setOtherName] = useState('')

  const docs = (attachments ?? []).filter(
    (a) => !a.is_deleted && !(a.mime_type || '').startsWith('image/'),
  )
  const byType = (t: string) => docs.find((d) => attachmentType(d) === t)
  const otherDocs = docs.filter((d) => !expectedKeys.has(attachmentType(d)))
  const presentCount = expected.filter((e) => byType(e.value)).length

  const uploadAs = async (type: string, files: File[]) => {
    if (!files.length) return
    setBusyType(type)
    try {
      for (const file of files) {
        await uploadDoc.mutateAsync({ file, docType: type })
      }
      toast.success('Document added')
    } catch (err) {
      toastApiError(err, 'Failed to add document')
    } finally {
      setBusyType(null)
    }
  }

  const pickFor = (type: string) => {
    pendingType.current = type
    inputRef.current?.click()
  }

  return (
    <DetailCard
      title="Documentation"
      icon={<FileText className="h-3.5 w-3.5" />}
      trailing={
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => setOpen((v) => !v)}
          className="flex h-auto cursor-pointer items-center gap-1.5 p-0 text-[10px] font-normal uppercase tracking-[0.12em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
        >
          {presentCount} of {expected.length}
          <ChevronDown
            size={13}
            className={open ? 'rotate-180 transition-transform' : 'transition-transform'}
          />
        </Button>
      }
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.currentTarget.files ?? [])
          if (files.length) void uploadAs(pendingType.current, files)
          e.currentTarget.value = ''
        }}
      />

      {!open ? (
        // Collapsed: a single quiet summary line.
        <Button
          type="button"
          variant="ghost"
          onClick={() => setOpen(true)}
          className="h-auto w-full justify-start rounded-none px-4 py-2 text-left text-[11px] font-normal text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
        >
          {presentCount === 0
            ? `No documents yet - click to add ${
                expected.map((e) => e.label).join(', ') || 'one'
              }.`
            : `${presentCount} of ${expected.length} key documents attached${
                otherDocs.length ? ` (+${otherDocs.length} other)` : ''
              } - click to manage.`}
        </Button>
      ) : (
        <div className="flex flex-col">
          {expected.map((e) => {
            const doc = byType(e.value)
            const busy = busyType === e.value
            return (
              <div
                key={e.value}
                className="flex items-center gap-2 border-b-[0.5px] border-[var(--rule-light)] px-4 py-2 text-[12px]"
              >
                {doc ? (
                  <Check size={13} className="shrink-0 text-[var(--green)]" />
                ) : (
                  <span className="h-[13px] w-[13px] shrink-0 rounded-full border-[1px] border-[var(--rule)]" />
                )}
                <span className="flex-1 truncate text-[var(--ink)]">
                  {e.label}
                </span>
                {doc ? (
                  <a
                    href={doc.file_url ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex max-w-[45%] items-center gap-1 truncate text-[11px] text-[var(--green)] hover:underline"
                  >
                    <span className="truncate">{doc.file_name}</span>
                    <ExternalLink size={10} className="shrink-0" />
                  </a>
                ) : null}
                {canEdit && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    disabled={busy}
                    onClick={() => pickFor(e.value)}
                    className="shrink-0 cursor-pointer gap-1 p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
                  >
                    {busy ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : doc ? (
                      'Replace'
                    ) : (
                      'Add'
                    )}
                  </Button>
                )}
              </div>
            )
          })}

          {/* Other uploaded documents */}
          {otherDocs.map((d) => (
            <div
              key={d.uuid}
              className="flex items-center gap-2 border-b-[0.5px] border-[var(--rule-light)] px-4 py-2 text-[12px]"
            >
              <Paperclip size={12} className="shrink-0 text-[var(--ink-3)]" />
              <span className="flex-1 truncate text-[var(--ink-2)]">
                {d.file_name}
              </span>
              {d.file_url && (
                <a
                  href={d.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-[var(--green)] hover:underline"
                >
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
          ))}

          {/* Other: name it + attach */}
          {canEdit && (
            <div className="flex items-center gap-2 px-4 py-2">
              <Input
                value={otherName}
                onChange={(ev) => setOtherName(ev.target.value)}
                placeholder="Other document name (e.g. Roof report)"
                className="h-7 flex-1 text-[12px]"
              />
              <Button
                type="button"
                variant="ghost"
                size="xs"
                disabled={busyType === '__other__'}
                onClick={() => {
                  pendingType.current = otherName.trim() || 'other'
                  inputRef.current?.click()
                  setOtherName('')
                }}
                className="shrink-0 cursor-pointer gap-1 p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--green)] hover:bg-transparent hover:opacity-70"
              >
                <Plus size={12} />
                Add
              </Button>
            </div>
          )}
        </div>
      )}
    </DetailCard>
  )
}
